import { ProductAccountDTO } from 'api-models'
import { UserAvatar } from 'components'
import { GButton } from 'components/basic-blocks'
import { GBadge } from 'components/basic-blocks/g-badge'
import { GDateTime } from 'components/basic-blocks/g-datetime'
import { GTableColumn } from 'components/basic-blocks/g-table/types'
import { PlanNames } from 'config'
import { useStores } from 'hooks'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { RiCloseLine, RiLoginCircleLine } from 'react-icons/ri'
import { useNavigate } from 'react-router-dom'
import { formatCurrency, formatPriceInterval, formatSpend } from 'utils'
import { planTierIconMapper } from 'utils/icons'

export const columns: Array<GTableColumn<ProductAccountDTO>> = [
	{
		label: 'Workspace',
		path: 'workspace',
		render: (row: ProductAccountDTO) => <WorkspaceColumn row={row} />,
		expand: true,
		className: 'justify-start'
	},
	{
		label: 'Owner',
		path: 'owner',
		render: (row: ProductAccountDTO) => <OwnerColumn row={row} />,
		className: 'justify-start'
	},
	{
		label: 'Plan price',
		path: 'planPrice',
		render: (row: ProductAccountDTO) => <PlanPriceColumn row={row} />,
		className: 'justify-start'
	},
	{
		label: 'Plan ad spend',
		path: 'planAdSpend',
		render: (row: ProductAccountDTO) => <PlanAdSpend row={row} />,
		className: 'justify-start'
	},
		{
		label: 'Last failed payment',
		path: 'lastFailedPayment',
		render: (row: ProductAccountDTO) => <LastFailedPayment row={row} />,
		className: 'justify-start'
	}
]

const WorkspaceColumn = (props: { row: ProductAccountDTO }) => {
	const { row } = props
	const navigate = useNavigate()

	const link = `/admin/subscription/${props.row.id}`
	const planName = PlanNames[props.row?.billing?.subscription?.plan.tier || '']
	const planIcon = planTierIconMapper(
		props.row.billing?.subscription?.plan.tier,
		6
	)

	return (
		<div className="flex justify-center items-center space-x-4">
			<>
				{planIcon}
				<div className="flex flex-col justify-center">
					<div
						className="font-medium text-primary-500 hover:text-primary-600 cursor-pointer"
						onClick={() => navigate(link)}
					>
						{row.name}
					</div>
					<div className="text-xs text-gray-500">{planName || ''}</div>
				</div>
			</>
		</div>
	)
}

const OwnerColumn = (props: { row: ProductAccountDTO }) => {
	const { row } = props
	const navigate = useNavigate()
	const { adminStore } = useStores()
	const [loading, setLoading] = useState(false)

	const link = `/admin/user/${row.user_info?.user_id}`
	const name = row.user_info?.name
	const email = row.user_info?.email

	const impersonate = async () => {
		setLoading(true)
		try {
			await adminStore.setImpersonation(row.user_info?.user_id || '')
			setLoading(false)
			navigate('/')
		} catch (e) {
			setLoading(false)
		}
	}

	useEffect(() => {
		return () => {
			setLoading(false)
		}
	}, [])

	return (
		<div className="flex space-x-2 items-center">
			<div className="flex items-center mr-2">
				<GButton
					shape="square"
					type="icon"
					loading={loading}
					icon={RiLoginCircleLine}
					variant="contained"
					color="primary"
					size="xs"
					onClick={impersonate}
				>
					Support View
				</GButton>
			</div>
			<div className="flex space-x-2 items-center">
				<UserAvatar size={8} src={row.user_info?.avatar} />
				<div
					className="flex flex-col group cursor-pointer"
					onClick={() => navigate(link)}
				>
					<div className="font-semibold text-primary-500 group-hover:text-primary-600">
						{name}
					</div>
					<div className="text-gray-500">{email}</div>
				</div>
			</div>
		</div>
	)
}

const PlanPriceColumn = (props: { row: ProductAccountDTO }) => {
	const { row } = props
	const isTrial = row.billing?.subscription?.type === 'TRIAL'
	const plan = isTrial
		? row.billing?.subscription?.next_plan
		: row.billing?.subscription?.plan

	return (
		<>
			{plan?.price ? (
				<GBadge
					text={
						<span>
							{formatCurrency(plan.price)}{' '}
							{formatPriceInterval(plan.interval, plan.interval_count)}
						</span>
					}
					color="gray"
				/>
			) : (
				<div className="flex items-center w-full">
					<RiCloseLine className="h-5 w-5 mr-2 text-t-secondary" />
					None
				</div>
			)}
		</>
	)
}

const PlanAdSpend = (props: { row: ProductAccountDTO }) => {
	const { row } = props
	const isTrial = row.billing?.subscription?.type === 'TRIAL'
	const plan = isTrial
		? row.billing?.subscription?.next_plan
		: row.billing?.subscription?.plan

	return (
		<>
			{plan?.spend ? (
				<GBadge text={<span>{formatSpend(plan?.spend)}</span>} color="gray" />
			) : (
				<RiCloseLine className="h-5 w-5 mr-2 text-t-secondary" />
			)}
		</>
	)
}

const LastFailedPayment = (props: { row: ProductAccountDTO }) => {
	const { row } = props

	return (
		<>
			{row.last_payment_failed ? (
				<div className="flex flex-col gap-1">
					<div className="text-xs text-gray-500">
						{moment(row.last_payment_failed).format('MMMM Do, YYYY - hh:mm:ss A')}
					</div>
					<div className="font-medium text-gray-400">
						<GDateTime date={row.last_payment_failed} />
					</div>
				</div>
			) : (
				<RiCloseLine className="h-5 w-5 mr-2 text-t-secondary" />
			)}
		</>
	)
}
